import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { apiService } from '../../services/api'
import { ArrowLeft, Truck, Package, CheckCircle, Clock, AlertCircle, Plus, Eye } from 'lucide-react'
import { toast } from 'react-toastify'

interface Fulfillment {
  id: string
  poId: string
  status: 'PENDING' | 'SHIPPED' | 'IN_TRANSIT' | 'DELIVERED' | 'CANCELLED'
  trackingNumber?: string
  trackingUrl?: string
  deliveryNotes?: string
  createdAt: string
  updatedAt: string
  po: {
    id: string
    poNumber: string
    totalAmount: number
    currency: string
    buyer: {
      companyName: string
    }
    vendor: {
      companyName: string
    }
  }
}

const FulfillmentPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [fulfillments, setFulfillments] = useState<Fulfillment[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedFulfillment, setSelectedFulfillment] = useState<Fulfillment | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/auth/login')
      return
    }
    loadFulfillments()
  }, [user, navigate])

  const loadFulfillments = async () => {
    try {
      setLoading(true)
      const response = await apiService.get('/api/post-quote-activity/fulfillments')
      setFulfillments((response as any).data || [])
    } catch (error: any) {
      console.error('Error loading fulfillments:', error)
      toast.error(error.response?.data?.message || 'Failed to load fulfillments')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'SHIPPED': return 'bg-blue-100 text-blue-800'
      case 'IN_TRANSIT': return 'bg-purple-100 text-purple-800'
      case 'DELIVERED': return 'bg-green-100 text-green-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PENDING': return <Clock className="w-4 h-4" />
      case 'SHIPPED': return <Package className="w-4 h-4" />
      case 'IN_TRANSIT': return <Truck className="w-4 h-4" />
      case 'DELIVERED': return <CheckCircle className="w-4 h-4" />
      case 'CANCELLED': return <AlertCircle className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading fulfillments...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/enterprise/post-quote-activity')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Fulfillment & Delivery Tracking</h1>
                <p className="text-gray-600 mt-1">Track shipments and manage deliveries</p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Create Fulfillment</span>
            </button>
          </div>
        </div>

        {/* Fulfillments List */}
        {fulfillments.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Truck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Fulfillments Yet</h3>
            <p className="text-gray-600 mb-6">
              Start tracking your shipments and deliveries by creating your first fulfillment record.
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg"
            >
              Create First Fulfillment
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {fulfillments.map((fulfillment) => (
              <div key={fulfillment.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      PO: {fulfillment.po.poNumber}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {fulfillment.po.buyer.companyName} → {fulfillment.po.vendor.companyName}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getStatusColor(fulfillment.status)}`}>
                      {getStatusIcon(fulfillment.status)}
                      <span>{fulfillment.status.replace('_', ' ')}</span>
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500">Order Value</p>
                    <p className="font-semibold">
                      {formatCurrency(fulfillment.po.totalAmount, fulfillment.po.currency)}
                    </p>
                  </div>
                  {fulfillment.trackingNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Tracking Number</p>
                      <p className="font-semibold">{fulfillment.trackingNumber}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="font-semibold">{formatDate(fulfillment.updatedAt)}</p>
                  </div>
                </div>

                {fulfillment.deliveryNotes && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500">Delivery Notes</p>
                    <p className="text-gray-700">{fulfillment.deliveryNotes}</p>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-500">
                    Created {formatDate(fulfillment.createdAt)}
                  </p>
                  <div className="flex items-center space-x-2">
                    {fulfillment.trackingUrl && (
                      <a
                        href={fulfillment.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                      >
                        Track Package
                      </a>
                    )}
                    <button
                      onClick={() => {
                        setSelectedFulfillment(fulfillment)
                        setShowDetailModal(true)
                      }}
                      className="text-gray-600 hover:text-gray-700 p-1"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Fulfillment Modal */}
      {showCreateModal && (
        <CreateFulfillmentModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false)
            loadFulfillments()
          }}
        />
      )}

      {/* Fulfillment Detail Modal */}
      {showDetailModal && selectedFulfillment && (
        <FulfillmentDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false)
            setSelectedFulfillment(null)
          }}
          fulfillment={selectedFulfillment}
          onUpdate={loadFulfillments}
        />
      )}
    </div>
  )
}

// Create Fulfillment Modal Component
const CreateFulfillmentModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    poId: '',
    trackingNumber: '',
    trackingUrl: '',
    deliveryNotes: ''
  })
  const [loading, setLoading] = useState(false)
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([])

  useEffect(() => {
    if (isOpen) {
      loadPurchaseOrders()
    }
  }, [isOpen])

  const loadPurchaseOrders = async () => {
    try {
      const response = await apiService.get('/api/post-quote-activity/purchase-orders')
      setPurchaseOrders((response as any).data?.filter((po: any) => po.status === 'ACCEPTED') || [])
    } catch (error) {
      console.error('Error loading purchase orders:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.poId) {
      toast.error('Please select a purchase order')
      return
    }

    try {
      setLoading(true)
      await apiService.post('/api/post-quote-activity/fulfillments', formData)
      toast.success('Fulfillment created successfully')
      onSuccess()
    } catch (error: any) {
      console.error('Error creating fulfillment:', error)
      toast.error(error.response?.data?.message || 'Failed to create fulfillment')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4">Create Fulfillment Record</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Order *
            </label>
            <select
              value={formData.poId}
              onChange={(e) => setFormData({ ...formData, poId: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Select Purchase Order</option>
              {purchaseOrders.map((po) => (
                <option key={po.id} value={po.id}>
                  {po.poNumber} - {po.buyer.companyName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tracking Number
            </label>
            <input
              type="text"
              value={formData.trackingNumber}
              onChange={(e) => setFormData({ ...formData, trackingNumber: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Enter tracking number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tracking URL
            </label>
            <input
              type="url"
              value={formData.trackingUrl}
              onChange={(e) => setFormData({ ...formData, trackingUrl: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="https://tracking.example.com/..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Delivery Notes
            </label>
            <textarea
              value={formData.deliveryNotes}
              onChange={(e) => setFormData({ ...formData, deliveryNotes: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
              placeholder="Additional delivery information..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Fulfillment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Fulfillment Detail Modal Component
const FulfillmentDetailModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  fulfillment: Fulfillment
  onUpdate: () => void
}> = ({ isOpen, onClose, fulfillment, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editData, setEditData] = useState({
    status: fulfillment.status,
    trackingNumber: fulfillment.trackingNumber || '',
    trackingUrl: fulfillment.trackingUrl || '',
    deliveryNotes: fulfillment.deliveryNotes || ''
  })
  const [loading, setLoading] = useState(false)

  const handleUpdateFulfillment = async () => {
    try {
      setLoading(true)
      await apiService.patch(`/api/post-quote-activity/fulfillments/${fulfillment.id}/status`, editData)
      toast.success('Fulfillment updated successfully')
      setIsEditing(false)
      onUpdate()
    } catch (error: any) {
      console.error('Error updating fulfillment:', error)
      toast.error(error.response?.data?.message || 'Failed to update fulfillment')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800'
      case 'SHIPPED': return 'bg-blue-100 text-blue-800'
      case 'IN_TRANSIT': return 'bg-purple-100 text-purple-800'
      case 'DELIVERED': return 'bg-green-100 text-green-800'
      case 'CANCELLED': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold">Fulfillment Details</h2>
          <div className="flex items-center space-x-2">
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded text-sm"
              >
                Update Status
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Purchase Order</p>
                              <p className="font-semibold">{fulfillment.po.poNumber}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              {isEditing ? (
                <select
                  value={editData.status}
                  onChange={(e) => setEditData({ ...editData, status: e.target.value as any })}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                >
                  <option value="PENDING">PENDING</option>
                  <option value="SHIPPED">SHIPPED</option>
                  <option value="IN_TRANSIT">IN TRANSIT</option>
                  <option value="DELIVERED">DELIVERED</option>
                </select>
              ) : (
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(fulfillment.status)}`}>
                  {fulfillment.status.replace('_', ' ')}
                </span>
              )}
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500">Tracking Information</p>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              {isEditing ? (
                <>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Tracking Number</label>
                    <input
                      type="text"
                      value={editData.trackingNumber}
                      onChange={(e) => setEditData({ ...editData, trackingNumber: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm"
                      placeholder="Enter tracking number"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Tracking URL</label>
                    <input
                      type="url"
                      value={editData.trackingUrl}
                      onChange={(e) => setEditData({ ...editData, trackingUrl: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-1 text-sm"
                      placeholder="Enter tracking URL"
                    />
                  </div>
                </>
              ) : (
                <>
                  {fulfillment.trackingNumber ? (
                    <p className="font-semibold">{fulfillment.trackingNumber}</p>
                  ) : (
                    <p className="text-gray-400 text-sm">No tracking number set</p>
                  )}
                  {fulfillment.trackingUrl && (
                    <a
                      href={fulfillment.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 text-sm"
                    >
                      Track Package →
                    </a>
                  )}
                </>
              )}
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-500">Delivery Notes</p>
            <div className="bg-gray-50 rounded-lg p-4">
              {isEditing ? (
                <textarea
                  value={editData.deliveryNotes}
                  onChange={(e) => setEditData({ ...editData, deliveryNotes: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm resize-none"
                  rows={3}
                  placeholder="Enter delivery notes"
                />
              ) : (
                <p>{fulfillment.deliveryNotes || 'No delivery notes'}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">Created</p>
              <p className="font-semibold">{new Date(fulfillment.createdAt).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Last Updated</p>
              <p className="font-semibold">{new Date(fulfillment.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-6 border-t border-gray-200">
          {isEditing ? (
            <>
              <button
                onClick={() => {
                  setIsEditing(false)
                  setEditData({
                    status: fulfillment.status,
                    trackingNumber: fulfillment.trackingNumber || '',
                    trackingUrl: fulfillment.trackingUrl || '',
                    deliveryNotes: fulfillment.deliveryNotes || ''
                  })
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFulfillment}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                disabled={loading}
              >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default FulfillmentPage